import type { AdminAiScanTreeSummaryProps } from "../../models/admin-ai-scans.models";
import { adminAiScansStyles } from "../../styles/admin-ai-scans.styles";
import { ADMIN_AI_SCAN_TONE_CLASSES } from "../../utils/admin-ai-scans.constants";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

// Align each hierarchy level's identity, counts and disclosure control like the original HTML reports.
const AdminAiScanTreeSummary = ({ label, id, meta, kind, counts }: AdminAiScanTreeSummaryProps) => (
  <summary className={adminAiScansStyles.treeSummary} data-test="admin-ai-scan-tree-summary">
    <span className="flex min-w-0 items-start gap-3">
      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" aria-hidden="true" />
      <span className="min-w-0">
        <span className="block break-words text-sm font-semibold leading-6">
          {label}
          {kind && (
            <span className="ml-2 rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase text-slate-600 dark:bg-gray-700 dark:text-gray-300">
              {kind}
            </span>
          )}
        </span>
        <span className="mt-0.5 block break-words font-mono text-[11px] font-normal leading-5 text-slate-500 dark:text-gray-400">
          {id}
          {meta && ` · ${meta}`}
        </span>
      </span>
    </span>
    {counts.length > 0 && (
      <span className="col-start-1 row-start-2 flex flex-wrap gap-2 pl-4 xl:col-start-2 xl:row-start-1 xl:justify-end xl:pl-0">
        {counts.map((count) => (
          <span
            key={count.label}
            className={twMerge(
              "inline-flex min-w-[48px] flex-col items-center rounded-lg px-2 py-1",
              count.severity ? ADMIN_AI_SCAN_TONE_CLASSES[count.severity] : "text-slate-900 dark:text-gray-100",
            )}
          >
            <span className="text-lg font-bold tabular-nums leading-5">{count.value.toLocaleString()}</span>
            <span className="mt-1 text-[9px] font-semibold uppercase leading-3 tracking-wide">{count.label}</span>
          </span>
        ))}
      </span>
    )}
    <ChevronDownIcon
      aria-hidden="true"
      className="col-start-2 row-start-1 h-4 w-4 text-slate-500 transition-transform xl:col-start-3"
    />
  </summary>
);

export default AdminAiScanTreeSummary;
